import type { DashboardSummary, Machine } from '@bridgewerk/shared';
import { StatCard } from './StatCard.js';

export interface StatRowProps {
  readonly summary: DashboardSummary | undefined;
  readonly machines: Machine[] | undefined;
  readonly loading?: boolean;
}

export function StatRow({ summary, machines, loading }: StatRowProps): JSX.Element {
  const total = summary?.machine_count ?? machines?.length ?? 0;
  const offline = summary?.offline_count ?? machines?.filter((m) => m.status === 'offline').length ?? 0;
  const online = total - offline;
  const running = summary?.running_count ?? machines?.filter((m) => m.status === 'running').length ?? 0;
  const oee = summary?.avg_oee_percent ?? 0;
  const alarms = summary?.active_alarm_count ?? machines?.filter((m) => m.status === 'alarm').length ?? 0;

  const oeeWarn = oee < 60;
  const alarmPulse = alarms > 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <StatCard
        label="Machines online"
        value={`${online} / ${total}`}
        color="accent"
        subtext={total === 0 ? 'no machines connected' : `${offline} offline`}
        loading={loading}
      />
      <StatCard
        label="Running now"
        value={running}
        color="green"
        subtext={total === 0 ? '—' : `${Math.round((running / Math.max(total, 1)) * 100)}% of fleet`}
        loading={loading}
      />
      <StatCard
        label="Avg OEE today"
        value={`${oee.toFixed(1)}%`}
        color={oeeWarn ? 'orange' : 'green'}
        subtext={oeeWarn ? 'below 60% target' : 'on target'}
        werkOs
        loading={loading}
      />
      <StatCard
        label="Active alarms"
        value={alarms}
        color="red"
        subtext={alarms === 0 ? 'all clear' : 'requires attention'}
        pulse={alarmPulse}
        loading={loading}
      />
    </div>
  );
}
